import { useState } from 'react'
import {
  Box,
  Typography,
  TextField,
  Button,
  Card,
  CardContent,
  Grid,
  IconButton,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Chip,
  CircularProgress,
  Autocomplete,
} from '@mui/material'
import {
  Add as AddIcon,
  Delete as DeleteIcon,
  Edit as EditIcon,
  Psychology as PsychologyIcon,
} from '@mui/icons-material'
import { Client, CliftonStrength, clientsApi } from '../../services/api'
import { useNotification } from '../../hooks/useNotification'
import { portalCliftonStrengthsSummary } from '../../utils/cliftonStrengths'

interface CliftonStrengthsFormProps {
  client: Client
  onUpdate: (client: Partial<Client>) => void
  /** Speaks to the client themselves rather than about them, with touch-sized controls. */
  isPortal?: boolean
}

type StrengthDomain = CliftonStrength['domain']

// All 34 CliftonStrengths themes grouped by domain
const CLIFTON_STRENGTHS: Record<StrengthDomain, string[]> = {
  executing: [
    'Achiever',
    'Arranger',
    'Belief',
    'Consistency',
    'Deliberative',
    'Discipline',
    'Focus',
    'Responsibility',
    'Restorative',
  ],
  influencing: [
    'Activator',
    'Command',
    'Communication',
    'Competition',
    'Maximizer',
    'Self-Assurance',
    'Significance',
    'Woo',
  ],
  relationship_building: [
    'Adaptability',
    'Connectedness',
    'Developer',
    'Empathy',
    'Harmony',
    'Includer',
    'Individualization',
    'Positivity',
    'Relator',
  ],
  strategic_thinking: [
    'Analytical',
    'Context',
    'Futuristic',
    'Ideation',
    'Input',
    'Intellection',
    'Learner',
    'Strategic',
  ],
}

// Create a flat list of all strengths with their domains
const ALL_STRENGTHS = Object.entries(CLIFTON_STRENGTHS).flatMap(([domain, names]) =>
  names.map((name) => ({ name, domain: domain as StrengthDomain }))
)

const domainLabels: Record<StrengthDomain, string> = {
  executing: 'Executing',
  influencing: 'Influencing',
  relationship_building: 'Relationship Building',
  strategic_thinking: 'Strategic Thinking',
}

const domainColors: Record<StrengthDomain, string> = {
  executing: '#8b5cf6',
  influencing: '#3b82f6',
  relationship_building: '#10b981',
  strategic_thinking: '#f59e0b',
}

interface StrengthFormData {
  name: string
  domain: StrengthDomain
  rank: number
  description: string
}

const emptyStrength: StrengthFormData = {
  name: '',
  domain: 'executing',
  rank: 1,
  description: '',
}

export default function CliftonStrengthsForm({
  client,
  onUpdate,
  isPortal,
}: CliftonStrengthsFormProps) {
  const { showSuccess, showError } = useNotification()
  const [dialogOpen, setDialogOpen] = useState(false)
  const [editingStrength, setEditingStrength] = useState<CliftonStrength | null>(null)
  const [formData, setFormData] = useState<StrengthFormData>(emptyStrength)
  const [saving, setSaving] = useState(false)
  const [deleting, setDeleting] = useState<string | null>(null)
  // The toast is gone in six seconds; a client who looked away would otherwise
  // find the dialog still open with no hint why nothing saved.
  const [saveError, setSaveError] = useState<string | null>(null)

  const strengths = client.cliftonStrengths || []
  const isComplete = strengths.length >= 5

  // Get next available rank
  const getNextRank = () => {
    if (strengths.length === 0) return 1
    return Math.max(...strengths.map((s) => s.rank)) + 1
  }

  // Get strengths already added (to exclude from autocomplete)
  const usedStrengthNames = new Set(strengths.map((s) => s.name))
  const availableStrengths = ALL_STRENGTHS.filter((s) => !usedStrengthNames.has(s.name))

  const handleOpenDialog = (strength?: CliftonStrength) => {
    if (strength) {
      setEditingStrength(strength)
      setFormData({
        name: strength.name,
        domain: strength.domain,
        rank: strength.rank,
        description: strength.description || '',
      })
    } else {
      setEditingStrength(null)
      setFormData({
        ...emptyStrength,
        rank: getNextRank(),
      })
    }
    setSaveError(null)
    setDialogOpen(true)
  }

  const handleCloseDialog = () => {
    setDialogOpen(false)
    setEditingStrength(null)
    setFormData(emptyStrength)
  }

  const handleStrengthSelect = (
    _event: React.SyntheticEvent,
    value: { name: string; domain: StrengthDomain } | null
  ) => {
    if (value) {
      setFormData({
        ...formData,
        name: value.name,
        domain: value.domain,
      })
    }
  }

  const handleSaveStrength = async () => {
    if (!formData.name) {
      showError('Please select a strength')
      return
    }

    try {
      setSaving(true)
      setSaveError(null)
      // Trim before persisting so a stray leading/trailing keystroke in the
      // description field never gets saved as if it were real content.
      const payload = { ...formData, description: formData.description.trim() }
      let result
      if (editingStrength) {
        result = await clientsApi.updateCliftonStrength(client.id, editingStrength.id, payload)
      } else {
        result = await clientsApi.addCliftonStrength(client.id, payload)
      }
      onUpdate({
        cliftonStrengths: result.cliftonStrengths,
        cliftonStrengthsCompletedAt: result.isComplete ? new Date().toISOString() : undefined,
      })
      showSuccess(editingStrength ? 'Strength updated' : 'Strength added')
      handleCloseDialog()
    } catch (err) {
      console.error('Error saving strength:', err)
      const message = err instanceof Error ? err.message : 'Failed to save strength'
      setSaveError(message)
      showError(message)
    } finally {
      setSaving(false)
    }
  }

  const handleDeleteStrength = async (strengthId: string) => {
    try {
      setDeleting(strengthId)
      const result = await clientsApi.deleteCliftonStrength(client.id, strengthId)
      onUpdate({
        cliftonStrengths: result.cliftonStrengths,
        cliftonStrengthsCompletedAt: result.isComplete
          ? client.cliftonStrengthsCompletedAt
          : undefined,
      })
      showSuccess('Strength deleted')
    } catch (err) {
      console.error('Error deleting strength:', err)
      showError(err instanceof Error ? err.message : 'Failed to delete strength')
    } finally {
      setDeleting(null)
    }
  }

  // The edit and delete icons are 34px by default, under the 44px floor a thumb
  // needs; the staff record keeps its compact size.
  const touchTarget = isPortal ? { width: 44, height: 44 } : undefined

  // Sort strengths by rank
  const sortedStrengths = [...strengths].sort((a, b) => a.rank - b.rank)

  return (
    <Box>
      {/* Progress Alert */}
      <Alert
        severity={isComplete ? 'success' : 'info'}
        icon={<PsychologyIcon />}
        sx={{ mb: 3 }}
      >
        {isPortal
          ? portalCliftonStrengthsSummary(strengths.length)
          : isComplete
          ? `Great! ${strengths.length} Clifton Strengths documented. The client is ready for the next stage.`
          : `${strengths.length}/5 Clifton Strengths documented. Add at least 5 to complete this section.`}
      </Alert>

      {/* Strengths List */}
      <Box sx={{ mb: 3 }}>
        {strengths.length === 0 ? (
          <Card variant="outlined" sx={{ p: 4, textAlign: 'center' }}>
            <PsychologyIcon sx={{ fontSize: 48, color: 'text.disabled', mb: 2 }} />
            <Typography color="text.secondary" sx={{ mb: 2 }}>
              {isPortal
                ? 'You have not added any Clifton Strengths yet. Add your top 5 from your CliftonStrengths results.'
                : 'No Clifton Strengths yet. Add at least 5 strengths from the CliftonStrengths assessment.'}
            </Typography>
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => handleOpenDialog()}
              sx={isPortal ? { minHeight: 44 } : undefined}
            >
              Add First Strength
            </Button>
          </Card>
        ) : (
          <Grid container spacing={2}>
            {sortedStrengths.map((strength) => (
              <Grid size={{ xs: 12, sm: 6, md: 4 }} key={strength.id}>
                <Card variant="outlined" sx={{ height: '100%' }}>
                  <CardContent>
                    <Box
                      sx={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'flex-start',
                        mb: 1,
                      }}
                    >
                      {/* Two cards sit side by side from the sm breakpoint up,
                          which in this record's column can be about 100px of
                          usable width, so the rank and the name have to be
                          allowed to stack and the name to break. */}
                      <Box sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', minWidth: 0, gap: 1 }}>
                        <Typography
                          variant="h6"
                          sx={{
                            bgcolor: 'grey.200',
                            borderRadius: '50%',
                            width: 28,
                            height: 28,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            fontSize: 14,
                            fontWeight: 600,
                          }}
                        >
                          {strength.rank}
                        </Typography>
                        <Typography
                          variant="subtitle1"
                          fontWeight={600}
                          sx={{ minWidth: 0, overflowWrap: 'anywhere' }}
                        >
                          {strength.name}
                        </Typography>
                      </Box>
                      <Box>
                        <IconButton
                          size="small"
                          aria-label={`Edit ${strength.name}`}
                          onClick={() => handleOpenDialog(strength)}
                          sx={touchTarget}
                        >
                          <EditIcon fontSize="small" />
                        </IconButton>
                        <IconButton
                          size="small"
                          color="error"
                          aria-label={`Delete ${strength.name}`}
                          onClick={() => handleDeleteStrength(strength.id)}
                          disabled={deleting === strength.id}
                          sx={touchTarget}
                        >
                          {deleting === strength.id ? (
                            <CircularProgress size={18} />
                          ) : (
                            <DeleteIcon fontSize="small" />
                          )}
                        </IconButton>
                      </Box>
                    </Box>

                    <Chip
                      label={domainLabels[strength.domain]}
                      size="small"
                      sx={{
                        bgcolor: domainColors[strength.domain],
                        color: 'white',
                        mb: 1,
                      }}
                    />

                    {strength.description?.trim() && (
                      <Typography
                        variant="body2"
                        color="text.secondary"
                        sx={{ mt: 1, fontStyle: 'italic' }}
                      >
                        &ldquo;{strength.description.trim()}&rdquo;
                      </Typography>
                    )}
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
        )}
      </Box>

      {/* Add Button */}
      {strengths.length > 0 && (
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => handleOpenDialog()}
          sx={isPortal ? { minHeight: 44 } : undefined}
        >
          Add Strength
        </Button>
      )}

      {/* Edit Dialog */}
      <Dialog open={dialogOpen} onClose={handleCloseDialog} maxWidth="sm" fullWidth>
        <DialogTitle>
          {editingStrength ? 'Edit Clifton Strength' : 'Add Clifton Strength'}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 1 }}>
            {isPortal && saveError && (
              <Alert severity="error" sx={{ mb: 2 }}>
                {saveError}
              </Alert>
            )}
            <Autocomplete
              options={editingStrength ? ALL_STRENGTHS : availableStrengths}
              getOptionLabel={(option) => option.name}
              groupBy={(option) => domainLabels[option.domain]}
              value={
                formData.name
                  ? ALL_STRENGTHS.find((s) => s.name === formData.name) || null
                  : null
              }
              onChange={handleStrengthSelect}
              disabled={!!editingStrength}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Strength"
                  required
                  placeholder="Select a CliftonStrength..."
                />
              )}
              renderOption={(props, option) => {
                const { key, ...rest } = props
                return (
                  <Box component="li" key={key} {...rest}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Box
                        sx={{
                          width: 8,
                          height: 8,
                          borderRadius: '50%',
                          bgcolor: domainColors[option.domain],
                        }}
                      />
                      {option.name}
                    </Box>
                  </Box>
                )
              }}
              sx={{ mb: 2 }}
            />

            <TextField
              fullWidth
              label="Rank"
              type="number"
              value={formData.rank}
              onChange={(e) =>
                setFormData({ ...formData, rank: parseInt(e.target.value) || 1 })
              }
              inputProps={{ min: 1, max: 34 }}
              sx={{ mb: 2 }}
              helperText="1 = strongest (typically your Top 5 are ranked 1-5)"
            />

            <TextField
              fullWidth
              label="How do you apply this strength? (optional)"
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              multiline
              rows={3}
              placeholder={
                isPortal
                  ? 'Describe how you use this strength in your work...'
                  : 'Describe how the client uses this strength in their work...'
              }
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog} sx={isPortal ? { minHeight: 44 } : undefined}>
            Cancel
          </Button>
          <Button
            onClick={handleSaveStrength}
            sx={isPortal ? { minHeight: 44 } : undefined}
            variant="contained"
            disabled={saving || !formData.name}
            startIcon={saving && <CircularProgress size={20} color="inherit" />}
          >
            {saving ? 'Saving...' : editingStrength ? 'Update' : 'Add'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  )
}
